/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import type { SelectedImageObjectType } from "@/types";
import PdfIcon from "@/assets/pdf-icon";
import PdfViewer from "../base64-pdf-viewer/base64-pdf-viewer";
import { PdfFallbackNotice } from "../pdf-fallback-notice";
import { selectPdfViewerMode } from "@/lib/pdf-viewer-support";

type ImageCarouselProps = {
  images: SelectedImageObjectType[];
  isOpen: boolean;
  showFooterActionButtons?: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  initialIndex?: number;
};

export function isPDF(image: SelectedImageObjectType) {
  if (image?.image instanceof File) {
    return image?.image?.type === "application/pdf";
  }
  return image?.imageUrl?.toLowerCase().endsWith(".pdf");
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  isOpen,
  showFooterActionButtons = true,
  onClose,
  onConfirm,
  initialIndex = 0,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isClosing, setIsClosing] = useState(false);
  const [{ mode: pdfViewerMode }] = useState(selectPdfViewerMode);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [hasPdfReadError, setHasPdfReadError] = useState(false);

  const currentImage = images?.[currentIndex];
  const currentPdfFile = isPDF(currentImage) ? currentImage?.image : undefined;

  useEffect(() => {
    // if (images?.length > 1) {
    //   setCurrentIndex(initialIndex);
    // }

    if (images?.length < currentIndex + 1) {
      setCurrentIndex(0);
    }
  }, [initialIndex, images?.length, currentIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setCurrentIndex(initialIndex);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images?.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images?.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen) return;
    switch (e.key) {
      case "Escape":
        handleClose();
        break;
      case "ArrowLeft":
        handlePrevious();
        break;
      case "ArrowRight":
        handleNext();
        break;
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /*
   * Read the picked File into memory for the app viewer. Only needed when the
   * browser cannot use its own plugin -- see selectPdfViewerMode(): a blob: URL
   * in an <iframe> is refused by the deployed frame-src on those browsers, and
   * no in-page element escapes it, because Chromium routes <embed>/<object>
   * plugins through a guest frame governed by the same directive. Rendering the
   * pages ourselves needs no plugin and no frame. See CSRTB-1034.
   */
  useEffect(() => {
    if (pdfViewerMode !== "app" || !currentPdfFile) {
      setPdfBytes(null);
      setHasPdfReadError(false);
      return;
    }

    let isStale = false;
    setPdfBytes(null);
    setHasPdfReadError(false);

    currentPdfFile
      .arrayBuffer()
      .then((buffer) => {
        if (!isStale) setPdfBytes(new Uint8Array(buffer));
      })
      .catch(() => {
        if (!isStale) setHasPdfReadError(true);
      });

    return () => {
      isStale = true;
    };
  }, [pdfViewerMode, currentPdfFile]);

  if (!isOpen && !isClosing) return null;

  const getImageSrc = (image: SelectedImageObjectType) => {
    return image.imageUrl;
  };

  if (!isOpen && !isClosing) return null;

  return (
    /*
      2.1.1 Keyboard — A: the overlay is a click-outside-to-dismiss affordance, not a
      control. It duplicates the labelled Close button below and must not be in the tab
      order or announced, so it is marked presentational rather than made focusable.
    */
    <div
      role="presentation"
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
        isClosing ? "animate-fade-out" : "animate-fade-in"
      }`}
      style={{
        backgroundColor: `hsl(var(--carousel-overlay))`,
      }}
      onClick={handleClose}
    >
      <div
        className="relative mx-auto flex size-full max-w-7xl flex-col p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-2">
          <div className="text-lg font-medium text-white">
            {currentIndex + 1} of {images?.length}
          </div>
          {/* 4.1.2 Name, Role, Value — A: icon-only controls render an <svg> with no text,
              so all three were announced as just "button". */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close image viewer"
            onClick={handleClose}
            className="size-10 rounded-full text-white hover:bg-white/10"
          >
            <X aria-hidden="true" style={{ height: 24, width: 24 }} />
          </Button>
        </div>

        {/* Image container */}
        <div className="relative mb-6 flex flex-1 items-center justify-center">
          {/* Arrows */}
          {images?.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Previous image"
                onClick={handlePrevious}
                className="absolute left-4 z-10 size-12 rounded-full bg-white/90 text-foreground shadow-lg hover:bg-white"
              >
                <ChevronLeft aria-hidden="true" className="size-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Next image"
                onClick={handleNext}
                className="absolute right-4 z-10 size-12 rounded-full bg-white/90 text-foreground shadow-lg hover:bg-white"
              >
                <ChevronRight aria-hidden="true" className="size-6" />
              </Button>
            </>
          )}

          {/* Current Image */}
          <div className="relative flex h-[70vh] w-full items-center justify-center">
            {isPDF(images?.[currentIndex]) ? (
              pdfViewerMode === "native" ? (
                <iframe
                  src={`${getImageSrc(
                    images?.[currentIndex],
                  )}#toolbar=0&navpanes=0&scrollbar=0`}
                  title={`PDF ${currentIndex + 1}`}
                  className="size-full rounded-lg bg-white shadow-2xl"
                  style={{
                    boxShadow: "var(--carousel-shadow)",
                  }}
                />
              ) : (
                <div
                  className="size-full overflow-auto rounded-lg bg-white"
                  style={{
                    boxShadow: "var(--carousel-shadow)",
                  }}
                >
                  <PdfFallbackNotice
                    downloadUrl={getImageSrc(images?.[currentIndex])}
                    downloadFileName={
                      currentPdfFile?.name ?? `document-${currentIndex + 1}.pdf`
                    }
                    compact
                  />
                  {hasPdfReadError ? (
                    <p className="p-4 text-center text-foreground">
                      This document could not be displayed.
                    </p>
                  ) : currentPdfFile ? (
                    pdfBytes ? (
                      <PdfViewer data={pdfBytes} maxWidth={1120} />
                    ) : null
                  ) : (
                    /* No File means a server-hosted document; react-pdf fetches it. */
                    <PdfViewer
                      url={getImageSrc(images?.[currentIndex])}
                      maxWidth={1120}
                    />
                  )}
                </div>
              )
            ) : (
              <img
                src={getImageSrc(images?.[currentIndex])}
                alt={images?.[currentIndex]?.image?.name}
                className="max-h-full max-w-full rounded-lg object-contain text-white shadow-2xl"
                style={{
                  boxShadow: "var(--carousel-shadow)",
                }}
              />
            )}
          </div>
        </div>

        {/* Thumbnails */}
        {images?.length > 1 && (
          <div className="mb-6 flex justify-center overflow-x-auto p-2">
            <div className="flex space-x-2 pb-2">
              {images?.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`size-16 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all duration-200 ${
                    index === currentIndex
                      ? "scale-110 border-primary"
                      : "border-white/30 hover:border-white/60"
                  }`}
                >
                  {isPDF(images?.[index]) ? (
                    <PdfIcon className="size-full object-cover p-3 text-white" />
                  ) : (
                    <img
                      src={getImageSrc(image)}
                      alt={`Thumbnail ${index + 1}`}
                      className="size-full object-cover"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action buttons */}
        {showFooterActionButtons ? (
          <div className="mb-4 flex justify-center space-x-4">
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-white/30 bg-white/10 px-8 text-white hover:border-white/50 hover:bg-white/20"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              className="hover:bg-primary/90 bg-primary px-8 text-primary-foreground"
            >
              Confirm Selection
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
